import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { WorkflowStatusNoteDialog } from '@/components/jobs/workflow-status-note-dialog'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { isCancelledWorkflowStatus, type RecurrenceActionScope } from '@/lib/job-recurrence'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import {
  getWorkflowTransitionLabel,
  workflowEntityLabel,
  workflowTransitionRequiresNote,
  workflowTransitionRequiresReason,
} from '@/lib/workflow-status-note'
import jobWorkflowApi from '@/services/job-workflow'
import type { JobWorkflowEntityType, JobWorkflowStatus, JobWorkflowTransition } from '@/types'

type WorkflowStatusSelectProps = {
  entityType: JobWorkflowEntityType
  entityId: string | number
  currentValue?: string | null
  currentStatusCode?: string | null
  disabled?: boolean
  className?: string
  contextDescription?: string
  onApplied?: () => void
  recurring?: boolean
  onSelectTransition?: (transition: JobWorkflowTransition, note?: string, scope?: RecurrenceActionScope) => void
}

const isUnsetWorkflowStatus = (currentStatusCode?: string | null, currentValue?: string | null) => {
  if (currentStatusCode == null || String(currentStatusCode).trim() === '') {
    return true
  }
  const label = String(currentValue ?? '').trim().toLowerCase()
  return !label || label === 'workflow not set' || label === 'unknown' || label === 'null'
}

const isBootstrapStatus = (status: JobWorkflowStatus) => (
  Boolean(status.is_default) || String(status.code ?? '').trim().toLowerCase() === 'draft'
)

const cancellationReasonEntityType = (entityType: JobWorkflowEntityType) => {
  if (entityType === 'job' || entityType === 'event' || entityType === 'project') return 'job'
  if (entityType === 'assignment') return 'assignment'
  if (entityType === 'workday_entry') return 'workday_entry'
  return null
}

type PendingNoteDialogState = {
  transition: JobWorkflowTransition
  isReason: boolean
  note: string
  scope?: RecurrenceActionScope
}

function statusAsSyntheticTransition(status: JobWorkflowStatus): JobWorkflowTransition {
  return {
    id: `status-${status.id}`,
    entity_type: status.entity_type,
    kind: status.kind,
    kind_code: status.kind_code,
    kind_label: status.kind_label,
    from_status: null,
    to_status: status.id,
    to_status_code: status.code,
    to_status_label: status.label || status.code,
    to_status_reason_required: Boolean(status.reason_required),
    requires_note: false,
    sorting_order: status.sorting_order,
  }
}

export function WorkflowStatusSelect({
  entityType,
  entityId,
  currentValue,
  currentStatusCode,
  disabled = false,
  className,
  contextDescription,
  onApplied,
  recurring = false,
  onSelectTransition,
}: WorkflowStatusSelectProps) {
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const [pendingNoteDialog, setPendingNoteDialog] = useState<PendingNoteDialogState | null>(null)
  const [pendingRecurrenceTransition, setPendingRecurrenceTransition] = useState<JobWorkflowTransition | null>(null)
  const unsetStatus = isUnsetWorkflowStatus(currentStatusCode, currentValue)
  const didRefetchJobAfterSync = useRef(false)

  const transitionsQuery = useQuery({
    queryKey: ['job-workflow-transitions', entityType, String(entityId)],
    queryFn: () => jobWorkflowApi.runtime.getStatusTransitions(entityType, entityId),
    enabled: Boolean(entityId),
  })
  const needsStatusOptions = Boolean(entityId) && (
    transitionsQuery.isError
    || (transitionsQuery.isSuccess && (transitionsQuery.data?.length ?? 0) === 0)
  )
  const statusOptionsQuery = useQuery({
    queryKey: ['job-workflow-status-options', entityType, String(entityId)],
    queryFn: () => jobWorkflowApi.runtime.getStatusOptions(entityType, entityId),
    enabled: needsStatusOptions,
  })

  // Transitions lookup may auto-seed Draft on the backend; refresh the job label.
  useEffect(() => {
    if (
      didRefetchJobAfterSync.current
      || !unsetStatus
      || !transitionsQuery.isSuccess
      || !['job', 'event', 'project'].includes(entityType)
    ) {
      return
    }
    didRefetchJobAfterSync.current = true
    queryClient.invalidateQueries({ queryKey: ['job', String(entityId)] })
  }, [entityId, entityType, queryClient, transitionsQuery.isSuccess, unsetStatus])
  const reasonEntityType = cancellationReasonEntityType(entityType)
  const cancellationReasonsQuery = useQuery({
    queryKey: ['job-cancellation-reasons', reasonEntityType, String(entityId)],
    queryFn: () => jobWorkflowApi.runtime.getCancellationReasons(reasonEntityType!, { entity_id: entityId }),
    enabled: Boolean(entityId) && reasonEntityType !== null,
  })

  const applyMutation = useMutation({
    mutationFn: ({ transition, note, scope }: { transition: JobWorkflowTransition; note?: string; scope?: RecurrenceActionScope }) => jobWorkflowApi.runtime.applyStatus({
      entity_type: entityType,
      entity_id: entityId,
      to_status: transition.to_status,
      note,
      scope,
    }),
    onSuccess: async () => {
      toast.success('Status updated')
      await queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', entityType, String(entityId)] })
      await queryClient.invalidateQueries({ queryKey: ['job-workflow-status-options', entityType, String(entityId)] })
      await queryClient.refetchQueries({ queryKey: ['job-workflow-transitions', entityType, String(entityId)], type: 'active' })

      if (entityType === 'assignment') {
        queryClient.invalidateQueries({ queryKey: ['assignment', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['assignments'] })
        queryClient.invalidateQueries({ queryKey: ['job-assignments'] })
        queryClient.invalidateQueries({ queryKey: ['worker-assignments'] })
      }

      if (entityType === 'workday_entry') {
        queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
        queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
        queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
        queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      }

      if (entityType === 'job' || entityType === 'event' || entityType === 'project') {
        queryClient.invalidateQueries({ queryKey: ['job', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['jobs'] })
      }

      onApplied?.()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const transitions = useMemo(() => {
    const configured = transitionsQuery.data ?? []
    if (configured.length > 0) {
      return configured
    }
    const options = statusOptionsQuery.data ?? []
    if (unsetStatus) {
      const bootstrap = options.filter(isBootstrapStatus)
      if (bootstrap.length > 0) {
        return bootstrap.map(statusAsSyntheticTransition)
      }
    }
    return options.map(statusAsSyntheticTransition)
  }, [statusOptionsQuery.data, transitionsQuery.data, unsetStatus])

  const isBusy = transitionsQuery.isLoading
    || (needsStatusOptions && statusOptionsQuery.isFetching && transitions.length === 0)
    || applyMutation.isPending
  const selectDisabled = disabled || isBusy || transitions.length === 0
  const currentLabel = unsetStatus
    ? (currentValue?.trim() || 'Not set')
    : (currentValue?.trim() || null)
  const placeholder = isBusy
    ? 'Loading statuses...'
    : transitions.length > 0
      ? currentLabel || (unsetStatus ? 'Set to Draft to get started' : 'Change status')
      : currentLabel || 'No statuses configured'

  const completeTransition = (transition: JobWorkflowTransition, note?: string, scope?: RecurrenceActionScope) => {
    if (onSelectTransition) {
      onSelectTransition(transition, note, scope)
      return
    }
    applyMutation.mutate({ transition, note, scope })
  }

  const continueTransition = (transition: JobWorkflowTransition, scope?: RecurrenceActionScope) => {
    if (workflowTransitionRequiresNote(transition)) {
      setPendingNoteDialog({
        transition,
        isReason: workflowTransitionRequiresReason(transition),
        note: '',
        scope,
      })
      return
    }
    completeTransition(transition, undefined, scope)
  }

  const handleTransitionSelected = (transition: JobWorkflowTransition) => {
    if (
      recurring
      && isCancelledWorkflowStatus(transition.to_status_code, transition.to_status_label || getWorkflowTransitionLabel(transition))
    ) {
      setPendingRecurrenceTransition(transition)
      return
    }
    continueTransition(transition)
  }

  const handleRecurrenceScope = (scope: RecurrenceActionScope) => {
    const transition = pendingRecurrenceTransition
    setPendingRecurrenceTransition(null)
    if (!transition) {
      return
    }
    continueTransition(transition, scope)
  }

  const handleNoteDialogConfirm = () => {
    if (!pendingNoteDialog) {
      return
    }

    const trimmedNote = pendingNoteDialog.note.trim()
    if (pendingNoteDialog.isReason && !trimmedNote) {
      toast.error('Reason is required for this status change')
      return
    }

    const { transition, scope } = pendingNoteDialog
    setPendingNoteDialog(null)
    completeTransition(transition, trimmedNote || undefined, scope)
  }

  return (
    <>
      <Select
        value=""
        disabled={selectDisabled}
        onValueChange={(value) => {
          const transition = transitions.find((item) => String(item.to_status) === value)
          if (!transition) {
            return
          }
          handleTransitionSelected(transition)
        }}
      >
        <SelectTrigger className={className}>
          {isBusy ? <Spinner className="mr-2 h-4 w-4" /> : null}
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {transitions.map((transition) => (
            <SelectItem key={String(transition.id)} value={String(transition.to_status)}>
              {getWorkflowTransitionLabel(transition)}
              {transition.to_status_reason_required ? ' (reason)' : ''}
              {transition.requires_note ? ' (note)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AlertDialog
        open={Boolean(pendingRecurrenceTransition)}
        onOpenChange={(open) => {
          if (!open && !applyMutation.isPending) {
            setPendingRecurrenceTransition(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This job is part of a recurrence. Cancel this job only, or the whole recurrence?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applyMutation.isPending}>Back</AlertDialogCancel>
            <Button
              type="button"
              variant="outline"
              disabled={applyMutation.isPending}
              onClick={() => handleRecurrenceScope('current')}
            >
              This job only
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={applyMutation.isPending}
              onClick={() => handleRecurrenceScope('recurrence')}
            >
              This recurrence
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WorkflowStatusNoteDialog
        open={Boolean(pendingNoteDialog)}
        onOpenChange={(open) => {
          if (!open && !applyMutation.isPending) {
            setPendingNoteDialog(null)
          }
        }}
        targetLabel={pendingNoteDialog ? getWorkflowTransitionLabel(pendingNoteDialog.transition) : ''}
        targetCode={pendingNoteDialog?.transition.to_status_code ?? null}
        isReason={Boolean(pendingNoteDialog?.isReason)}
        note={pendingNoteDialog?.note ?? ''}
        onNoteChange={(note) => {
          setPendingNoteDialog((current) => (current ? { ...current, note } : current))
        }}
        onConfirm={handleNoteDialogConfirm}
        isPending={applyMutation.isPending}
        contextDescription={contextDescription}
        entityLabel={entityType === 'job' ? labels.entity : workflowEntityLabel(entityType)}
        cancellationReasons={cancellationReasonsQuery.data ?? []}
      />
    </>
  )
}
